import { ctDisplaySetSelector, ptDisplaySetSelector } from '@common';
import { ServicesManager } from '@core';
import { DisplaySetMatchDetails } from '@core/types/HangingProtocol';

export interface FusionMatchingSeries {
	ctSeries?: DisplaySetMatchDetails[];
	ptSeries?: DisplaySetMatchDetails[];
}

// Modality groups for fusion pairing
export const ANATOMICAL_MODALITIES = ['CT', 'MR'];
export const FUNCTIONAL_MODALITIES = ['PT', 'NM'];

export interface FusionPair {
	backgroundDisplaySetUID: string;
	overlayDisplaySetUID: string;
	backgroundModality: string;
	overlayModality: string;
}

export interface FusionSeriesInfo {
	displaySetInstanceUID: string;
	SeriesDescription: string;
	Modality: string;
	SeriesNumber: number;
	SeriesDate?: string;
	SeriesTime?: string;
}

/**
 * Tìm cặp series phù hợp cho fusion theo BR3.
 * - Series gốc CT/MR → tìm PT/NM để làm overlay (gốc làm background)
 * - Series gốc PT/NM → tìm CT/MR để làm background (gốc làm overlay)
 *
 * @returns FusionPair hoặc null nếu không tìm thấy
 */
export const findFusionPair = (servicesManager: ServicesManager, baseDisplaySetInstanceUID: string): FusionPair | null => {
	const { displaySetService } = servicesManager.services;
	const baseDisplaySet = displaySetService.getDisplaySetByUID(baseDisplaySetInstanceUID);

	if (!baseDisplaySet) {
		return null;
	}

	const baseModality = baseDisplaySet.Modality;
	const isAnatomical = ANATOMICAL_MODALITIES.includes(baseModality);
	const isFunctional = FUNCTIONAL_MODALITIES.includes(baseModality);

	if (!isAnatomical && !isFunctional) {
		return null;
	}

	// Xác định modality cần tìm
	const targetModalities = isAnatomical ? FUNCTIONAL_MODALITIES : ANATOMICAL_MODALITIES;

	// Lấy tất cả displaySets khả dụng
	const allDisplaySets = displaySetService.getActiveDisplaySets();

	// Tìm series đối ứng: phải isReconstructable (BR2), đúng modality
	const candidates = allDisplaySets.filter((ds) => {
		if (ds.displaySetInstanceUID === baseDisplaySetInstanceUID) {
			return false;
		}
		if (!targetModalities.includes(ds.Modality)) {
			return false;
		}
		if (!ds.isReconstructable) {
			return false;
		}
		return true;
	});

	if (candidates.length === 0) {
		return null;
	}

	// Ưu tiên: cùng StudyInstanceUID → cùng FrameOfReferenceUID
	const sorted = candidates.sort((a, b) => {
		const aScore =
			(a.StudyInstanceUID === baseDisplaySet.StudyInstanceUID ? 100 : 0) +
			(a.FrameOfReferenceUID === baseDisplaySet.FrameOfReferenceUID ? 50 : 0);
		const bScore =
			(b.StudyInstanceUID === baseDisplaySet.StudyInstanceUID ? 100 : 0) +
			(b.FrameOfReferenceUID === baseDisplaySet.FrameOfReferenceUID ? 50 : 0);
		return bScore - aScore;
	});

	const matchedDisplaySet = sorted[0];

	// Theo BR3: PT/NM luôn là overlay, CT/MR luôn là background
	if (isAnatomical) {
		return {
			backgroundDisplaySetUID: baseDisplaySetInstanceUID,
			overlayDisplaySetUID: matchedDisplaySet.displaySetInstanceUID,
			backgroundModality: baseModality,
			overlayModality: matchedDisplaySet.Modality,
		};
	} else {
		return {
			backgroundDisplaySetUID: matchedDisplaySet.displaySetInstanceUID,
			overlayDisplaySetUID: baseDisplaySetInstanceUID,
			backgroundModality: matchedDisplaySet.Modality,
			overlayModality: baseModality,
		};
	}
};

/**
 * Lấy danh sách series khả dụng để chồng lớp (dùng cho UI series selector).
 * Chỉ trả về series có isReconstructable = true (BR2).
 */
export const getAvailableFusionSeries = (
	servicesManager: ServicesManager,
	excludeDisplaySetInstanceUID: string,
	targetModalities: string[],
): FusionSeriesInfo[] => {
	const { displaySetService } = servicesManager.services;
	const allDisplaySets = displaySetService.getActiveDisplaySets();

	return allDisplaySets
		.filter((ds) => {
			if (ds.displaySetInstanceUID === excludeDisplaySetInstanceUID) {
				return false;
			}
			if (!targetModalities.includes(ds.Modality)) {
				return false;
			}
			if (!ds.isReconstructable) {
				return false;
			}
			return true;
		})
		.map((ds) => ({
			displaySetInstanceUID: ds.displaySetInstanceUID,
			SeriesDescription: ds.SeriesDescription || '',
			Modality: ds.Modality,
			SeriesNumber: Number(ds.SeriesNumber) || 0,
			SeriesDate: ds.SeriesDate,
			SeriesTime: ds.SeriesTime,
		}));
};

export const calculateFusionMatchingSeries = (servicesManager: ServicesManager): FusionMatchingSeries => {
	const { hangingProtocolService } = servicesManager.services;

	const ctSeries = hangingProtocolService.matchImages(ctDisplaySetSelector);
	const ptSeries = hangingProtocolService.matchImages(ptDisplaySetSelector);

	return {
		ctSeries: ctSeries.matchingScores,
		ptSeries: ptSeries.matchingScores,
	};
};

export const isFusionable = (services: ServicesManager) => {
	const { ctSeries, ptSeries } = calculateFusionMatchingSeries(services);
	const fusionable = ctSeries && ptSeries && ctSeries.length > 0 && ptSeries.length > 0;
	return fusionable;
};
